// exportCommit.js - Redux Toolkit slice for export commit
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { cloneDeep } from "lodash";
import { exportCommitAPI } from "./api";
import { logout } from "../auth/index";
import { createMiddleware } from "../middlewareUtil";

// Initial state
const initialState = {
  exportStatus: "QUEUED",
  fileCount: 0,
  totalBytes: 0,
  guid: "",
  isDownloadReady: false,
  isDownloading: false,
  isExported: false,
  isExporting: false,
  percentComplete: 0,
};

export const queueDownloadExportCommit = createAsyncThunk(
  "exportCommit/queueDownloadExportCommit",
  async ({ caseId, clipId }, { getState, dispatch, rejectWithValue }) => {
    const accessToken = getState().auth.accessCode;

    const result = await exportCommitAPI.queueDownloadExportCommit(
      accessToken,
      caseId,
      clipId,
    );

    if (result.success) {
      return result.data;
    } else if (result.error === "unauthorized") {
      dispatch(logout());
      return rejectWithValue("Authentication failed");
    }

    return rejectWithValue("Failed to queue export commit download");
  },
);

// Create the slice
export const exportCommitSlice = createSlice({
  name: "exportCommit",
  initialState,
  reducers: {
    startDownloadExportCommit: (state, _action) => {
      state.isDownloadReady = true;
      state.isDownloading = false;
    },
    downloadingExportCommit: (state, _action) => {
      state.isDownloadReady = true;
      state.isDownloading = true;
    },
    statusUpdateExportCommit: (state, action) => {
      state.exportStatus = action.payload.status;
      state.guid = action.payload.guid;
      state.fileCount = action.payload.fileCount ?? state.fileCount;
      state.totalBytes = action.payload.totalBytes ?? state.totalBytes;
      state.isExporting = action.payload.status !== "DOWNLOADING_COMPLETE";
      state.isExported = action.payload.status === "DOWNLOADING_COMPLETE";
      state.isDownloading = action.payload.downloading === "DOWNLOADING";
      state.percentComplete = action.payload.percentComplete;
    },
    reset: (_state, _action) => {
      return cloneDeep(initialState);
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(queueDownloadExportCommit.pending, (state, _action) => {
        state.isExporting = true;
        state.isExported = false;
      })
      .addCase(queueDownloadExportCommit.fulfilled, (state, action) => {
        state.guid = action.payload.guid;
        state.fileCount = action.payload.fileCount ?? state.fileCount;
        state.totalBytes = action.payload.totalBytes ?? state.totalBytes;
        state.isExporting = false;
        state.isExported = true;
      })
      .addCase(queueDownloadExportCommit.rejected, (state, _action) => {
        state.isExporting = false;
        state.isExported = false;
      });
  },
});

export const exportCommitMiddleware = createMiddleware([
  "exportCommit/queueDownloadExportCommit/pending",
]);

// Export actions
export const {
  downloadingExportCommit,
  reset,
  startDownloadExportCommit,
  statusUpdateExportCommit,
} = exportCommitSlice.actions;

// Export reducer
export default exportCommitSlice.reducer;
